import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useJobStatus } from '@/hooks/useJobStatus';
import { useAppStore } from '@/store';
import * as api from '@/api/client';
import type { Claim, MatterFullState, Stage } from '@/types';
import { STAGE_ORDER } from '@/types';
import {
  RefreshCw, Lock, Maximize2, Minimize2,
  Loader2, ArrowRight, AlertTriangle, CheckCircle, Zap, Hash, Eye,
  History as HistoryIcon, RotateCcw, Check, CircleDot,
  PanelLeftClose, PanelLeftOpen, Plus, Trash2, X,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  evaluateClaimHealth, idfTextsFromState, computeHighlightRanges,
  type HealthResult, type HealthIssue,
} from '@/lib/claimHealth';
import StageGateBanner from '@/components/layout/StageGateBanner';
import StageAcknowledgment from '@/components/workspace/StageAcknowledgment';

/**
 * Left rail for Stage 3.
 *
 * Matches the PRD v3 planned UI: a STAGES section that mirrors the top-bar
 * progress (with status text — "Frozen" / "In progress" / "Locked") plus a
 * CLAIMS tree where dependents are rendered nested under their independent
 * parent with a vertical connector.
 */
function Stage3Sidebar({
  claims, activeClaimId, onSelect, matterState, onJumpToStage,
  collapsed, onToggleCollapse, onAddClaimFor, readOnly, healthByClaimId,
  qcFindingsByClaimNumber, onRelink,
}: {
  claims: Claim[];
  activeClaimId: string | null;
  onSelect: (id: string) => void;
  matterState: MatterFullState | null;
  onJumpToStage: (s: Stage) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onAddClaimFor: (preset: { kind: 'independent' | 'dependent'; parent_claim_number: number | null }) => void;
  readOnly: boolean;
  healthByClaimId: Map<string, HealthResult>;
  qcFindingsByClaimNumber: Map<number, any[]>;
  /** Triggered from the Unlinked Dependents section — re-parses
   *  every claim's text server-side via /claims/relink and refreshes
   *  the matter so the orphan rows disappear when their parents are
   *  picked up. */
  onRelink?: () => Promise<void>;
}) {
  // ── Hooks must run unconditionally on every render ──────────────
  // Local busy flag for the Re-link Dependencies button rendered
  // inside the Unlinked Dependents section below. Declared at the
  // very top of the component so the rules-of-hooks invariant holds
  // — the early-return for the collapsed view (below) used to sit
  // ahead of this useState, which short-circuited the hook on every
  // collapsed render and produced the classic "Rendered fewer hooks
  // than expected" React warning the moment the drafter expanded
  // the rail.
  const [relinking, setRelinking] = useState(false);

  // Collapsed view: thin rail with only the expand toggle. Gives the drafter
  // the full page width for the claim card (e.g., when reading a long claim
  // text with many placeholders).
  if (collapsed) {
    return (
      <div
        className="w-10 border-r border-zinc-200 bg-zinc-50 flex flex-col items-center pt-4 pb-6 flex-shrink-0"
        data-testid="claim-navigator-collapsed"
      >
        <button
          type="button"
          data-testid="sidebar-expand-button"
          onClick={onToggleCollapse}
          title="Expand sidebar"
          className="p-1.5 rounded-sm text-zinc-500 hover:text-[#002FA7] hover:bg-white transition-colors"
        >
          <PanelLeftOpen size={16} />
        </button>
      </div>
    );
  }
  const currentStage = matterState?.matter?.current_stage;
  const currentIdx = currentStage ? STAGE_ORDER.indexOf(currentStage) : -1;

  // Short labels for the STAGES list — the long-form labels in STAGE_LABELS
  // are tuned for the top bar; in the sidebar we mirror the planned UI.
  const stageMeta: Record<Stage, string> = {
    stage_0: 'Upload',
    stage_1: 'IDF understanding',
    stage_2: 'Novelty & IS',
    stage_2_5: 'Claim strategy',
    stage_3: 'Claim drafting',
    stage_4: 'QC & export',
  };

  const stageStatus = (stage: Stage): { label: string; accessible: boolean } => {
    const idx = STAGE_ORDER.indexOf(stage);
    const sstate = matterState?.stages?.[stage];
    const frozen = !!sstate?.is_frozen;
    if (frozen) {
      // Stage 2.5 uses "Added & locked" when the drafter saved a strategy;
      // "Skipped" when they chose to skip. Falls back to "Locked" if unset.
      if (stage === 'stage_2_5') {
        const hasStrategy = !!(sstate?.content?.strategy_text || '').trim();
        return { label: hasStrategy ? 'Added & locked' : 'Skipped', accessible: true };
      }
      return { label: 'Frozen', accessible: true };
    }
    if (idx === currentIdx) return { label: 'In progress', accessible: true };
    if (idx < currentIdx) return { label: 'Complete', accessible: true };
    return { label: 'Locked', accessible: false };
  };

  // Build the parent → dependents map. Dependents that reference an unknown
  // parent number (shouldn't happen after sanitisation, but defensive) are
  // shown as orphans at the bottom so they're still accessible.
  const independents = claims.filter(c => c.claim_type === 'independent');
  const dependentsByParent: Record<number, Claim[]> = {};
  const orphanDeps: Claim[] = [];
  for (const c of claims) {
    if (c.claim_type !== 'dependent') continue;
    const parent = c.parent_claim_number;
    if (parent && independents.some(i => i.claim_number === parent)) {
      (dependentsByParent[parent] ||= []).push(c);
    } else {
      orphanDeps.push(c);
    }
  }
  for (const deps of Object.values(dependentsByParent)) {
    deps.sort((a, b) => a.claim_number - b.claim_number);
  }

  return (
    <div
      className="w-72 border-r border-zinc-200 bg-zinc-50 overflow-y-auto flex-shrink-0 pt-4 pb-6"
      data-testid="claim-navigator"
    >
      {/* Collapse toggle — sits in the sidebar header so drafters can
          reclaim the horizontal space when reading a long claim. */}
      <div className="flex items-center justify-between px-4 mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Navigator</span>
        <button
          type="button"
          data-testid="sidebar-collapse-button"
          onClick={onToggleCollapse}
          title="Collapse sidebar"
          className="p-1 rounded-sm text-zinc-500 hover:text-[#002FA7] hover:bg-white transition-colors"
        >
          <PanelLeftClose size={14} />
        </button>
      </div>

      {/* ── STAGES ── */}
      <h3 className="px-4 text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Stages</h3>
      <div className="space-y-0.5 mb-6">
        {STAGE_ORDER.map(stage => {
          const { label, accessible } = stageStatus(stage);
          const isCurrent = stage === 'stage_3';
          const sstate = matterState?.stages?.[stage];
          const frozen = !!sstate?.is_frozen;
          // Icon: check for frozen/complete, filled dot for in-progress,
          // padlock for locked.
          const icon = frozen
            ? <Check size={12} className="text-green-600" />
            : isCurrent
              ? <CircleDot size={12} className="text-[#002FA7]" />
              : accessible
                ? <CircleDot size={12} className="text-zinc-400" />
                : <Lock size={12} className="text-zinc-400" />;
          return (
            <button
              key={stage}
              type="button"
              data-testid={`sidebar-stage-${stage}`}
              onClick={() => accessible && onJumpToStage(stage)}
              disabled={!accessible}
              className={`w-full px-4 py-1.5 text-left text-sm flex items-center gap-2 transition-colors ${
                isCurrent
                  ? 'bg-white border-r-2 border-[#002FA7] text-zinc-900'
                  : accessible
                    ? 'text-zinc-600 hover:bg-white/70'
                    : 'text-zinc-400 cursor-not-allowed'
              }`}
            >
              <span className="flex-shrink-0">{icon}</span>
              <span className="truncate flex-1">{stageMeta[stage]}</span>
              <span className={`text-[10px] font-mono uppercase tracking-wide flex-shrink-0 ${
                frozen ? 'text-green-700'
                  : isCurrent ? 'text-[#002FA7]'
                  : accessible ? 'text-zinc-400'
                  : 'text-zinc-300'
              }`}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* ── CLAIMS tree ── */}
      <h3 className="px-4 text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Claims</h3>
      <div>
        {independents.length === 0 && orphanDeps.length === 0 && (
          <div className="px-4 py-2 text-xs text-zinc-400">No claims yet.</div>
        )}

        {independents.map(ind => {
          const deps = dependentsByParent[ind.claim_number] || [];
          return (
            <div key={ind.id} className="mb-1">
              <ClaimRow
                claim={ind}
                activeClaimId={activeClaimId}
                onSelect={onSelect}
                health={healthByClaimId.get(ind.id) || null}
                qcCount={(qcFindingsByClaimNumber.get(ind.claim_number) || []).length}
                needsFreeze
              />
              {/* Tree connector + dependents + "+ Add dependent" at the
                  bottom of each subtree. */}
              <div className={`ml-5 ${deps.length > 0 ? 'border-l border-zinc-300' : ''} pl-0 mt-0.5`}>
                {deps.map(dep => (
                  <ClaimRow
                    key={dep.id}
                    claim={dep}
                    activeClaimId={activeClaimId}
                    onSelect={onSelect}
                    parentNumber={ind.claim_number}
                    health={healthByClaimId.get(dep.id) || null}
                    qcCount={(qcFindingsByClaimNumber.get(dep.claim_number) || []).length}
                  />
                ))}
                {!readOnly && (
                  <button
                    type="button"
                    data-testid={`add-dependent-to-${ind.claim_number}`}
                    onClick={() => onAddClaimFor({ kind: 'dependent', parent_claim_number: ind.claim_number })}
                    className="w-full px-3 py-1 text-left text-[11px] text-zinc-400 hover:text-[#002FA7] hover:bg-white/70 flex items-center gap-1 transition-colors"
                  >
                    <Plus size={11} /> Add dependent of claim {ind.claim_number}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {orphanDeps.length > 0 && (
          <div className="mt-2 border-t border-zinc-200 pt-2">
            <div className="px-4 text-[10px] font-mono uppercase tracking-wide text-zinc-400 mb-1">
              Unlinked dependents
            </div>
            {orphanDeps.map(dep => (
              <ClaimRow
                key={dep.id}
                claim={dep}
                activeClaimId={activeClaimId}
                onSelect={onSelect}
                health={healthByClaimId.get(dep.id) || null}
                qcCount={(qcFindingsByClaimNumber.get(dep.claim_number) || []).length}
              />
            ))}
            {/* Re-parse claim text server-side and rewrite
                depends_on_ids. Visible only when there ARE
                orphans (the section above gates on that). When
                the parse picks up parents that the post-generate
                relink missed (e.g. a partial-JSON recovery that
                landed mid-claim, or a freeform edit that changed
                the parent number) the orphan rows disappear on
                the next refresh. */}
            {!readOnly && onRelink && (
              <button
                type="button"
                data-testid="relink-dependencies-button"
                onClick={async () => {
                  if (relinking) return;
                  setRelinking(true);
                  try { await onRelink(); }
                  finally { setRelinking(false); }
                }}
                disabled={relinking}
                title="Re-parse claim text to resolve parent links"
                className="mt-1 w-full px-4 py-1.5 text-left text-xs text-[#002FA7] hover:bg-white/70 flex items-center gap-1.5 transition-colors disabled:opacity-50"
              >
                {relinking
                  ? <Loader2 size={12} className="animate-spin" />
                  : <RotateCcw size={12} />}
                {relinking ? 'Re-linking…' : 'Re-link Dependencies'}
              </button>
            )}
          </div>
        )}

        {!readOnly && (
          <div className="mt-3 border-t border-zinc-200 pt-2">
            <button
              type="button"
              data-testid="add-independent-claim"
              onClick={() => onAddClaimFor({ kind: 'independent', parent_claim_number: null })}
              className="w-full px-4 py-1.5 text-left text-xs text-[#002FA7] hover:bg-white/70 flex items-center gap-1.5 transition-colors"
            >
              <Plus size={12} /> Add independent claim
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Named props type — using a named type (instead of an inline object type in
// the function signature) keeps React's intrinsic `key` attribute handling
// working when the component is rendered inside a .map(). With the inline
// object type, strict TS flags `key` as an "unknown prop".
// Extracted component type so React.FC adds the implicit JSX intrinsic
// attributes (`key`, `ref`) — TS strict mode rejects `<ClaimRow key=… />`
// when the component uses a plain destructured-parameter type.
type ClaimRowProps = {
  claim: Claim;
  activeClaimId: string | null;
  onSelect: (id: string) => void;
  parentNumber?: number;
  health?: HealthResult | null;
  /** Number of QC findings referencing this claim_number — when > 0
   *  a red pip renders on the row so the drafter can see which
   *  claims need attention without opening every card. */
  qcCount?: number;
  /** When true, this row is an unfrozen independent claim that the
   *  Continue-to-QC gate is specifically blocking on. Used to draw
   *  a subtle "needs freeze" marker next to the row so the tree
   *  aligns with the blocked-reason banner at the top of Stage 3. */
  needsFreeze?: boolean;
};

const ClaimRow: React.FC<ClaimRowProps> = ({ claim, activeClaimId, onSelect, parentNumber, health, qcCount, needsFreeze }) => {
  const isActive = claim.id === activeClaimId;
  const isDep = claim.claim_type === 'dependent';
  // Label matches the planned UI:
  //   Independent  →  "Claim N — Method (Ind.)"
  //   Dependent    →  "Claim N — Dep. of M"
  const categoryLabel = claim.category === 'crm'
    ? 'CRM'
    : claim.category.charAt(0).toUpperCase() + claim.category.slice(1);
  const label = isDep
    ? `Claim ${claim.claim_number} — Dep. of ${parentNumber ?? claim.parent_claim_number ?? '?'}`
    : `Claim ${claim.claim_number} — ${categoryLabel} (Ind.)`;
  // Dependents get a slight left-padding inside the tree-connector column;
  // independents sit flush with the rail margin.
  const rowPad = isDep ? 'pl-3 pr-4' : 'px-4';
  // Health pip — green tick when all checks pass, yellow warning when
  // any health check flags an issue. Tooltip lists the flagged checks
  // so the drafter can see why without opening the card.
  const healthTooltip = health && health.status === 'warn'
    ? health.issues.map((i) => `• ${i.message}`).join('\n')
    : 'No issues found';
  return (
    <button
      type="button"
      data-testid={`claim-nav-${claim.claim_number}`}
      onClick={() => onSelect(claim.id)}
      className={`w-full ${rowPad} py-1.5 text-left text-sm flex items-center gap-2 transition-colors ${
        isActive ? 'bg-white border-r-2 border-[#002FA7] text-zinc-900' : 'text-zinc-600 hover:bg-white/70'
      }`}
    >
      {isDep && <span className="text-zinc-300 font-mono text-xs select-none">─</span>}
      <span className="truncate flex-1">{label}</span>
      {health && health.status === 'ok' && (
        <span
          data-testid={`claim-nav-health-ok-${claim.claim_number}`}
          title={healthTooltip}
          className="flex-shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-full bg-green-100 text-green-700"
        >
          <Check size={10} />
        </span>
      )}
      {health && health.status === 'warn' && (
        <span
          data-testid={`claim-nav-health-warn-${claim.claim_number}`}
          title={healthTooltip}
          className="flex-shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-100 text-amber-700"
        >
          <AlertTriangle size={10} />
        </span>
      )}
      {typeof qcCount === 'number' && qcCount > 0 && (
        <span
          data-testid={`claim-nav-qc-${claim.claim_number}`}
          title={`${qcCount} QC finding${qcCount === 1 ? '' : 's'} — see claim card`}
          className="flex-shrink-0 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-100 text-red-700 text-[10px] font-mono font-semibold"
        >
          {qcCount}
        </span>
      )}
      {needsFreeze && !claim.is_frozen && (
        <span
          data-testid={`claim-nav-needs-freeze-${claim.claim_number}`}
          title="Independent claim not frozen — required before Continue to QC"
          className="flex-shrink-0 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-amber-100 text-amber-800 text-[9px] font-mono uppercase tracking-wider"
        >
          Unfrozen
        </span>
      )}
      {claim.is_frozen && <Lock size={10} className="text-green-600 flex-shrink-0" />}
      {claim.placeholder_count > 0 && (
        <span className="flex-shrink-0 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-sm font-mono">
          {claim.placeholder_count}
        </span>
      )}
    </button>
  );
};

// Placeholder rendering lives in ClaimBodyRenderer below, which also
// weaves in health-check highlights. The old standalone
// PlaceholderRenderer has been retired — its chip component
// (PlaceholderChip) is still used by ClaimBodyRenderer.

function PlaceholderChip({ hint, fullMatch, onReplace, disabled }: {
  hint: string; fullMatch: string;
  onReplace: (original: string, replacement: string) => void;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  if (editing && !disabled) {
    return (
      <span className="inline-flex items-center gap-1 mx-1">
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && value.trim()) {
              onReplace(fullMatch, value.trim());
              setEditing(false);
            }
            if (e.key === 'Escape') setEditing(false);
          }}
          className="px-2 py-0.5 border border-amber-400 rounded-sm text-sm bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-500 w-48"
          placeholder={hint}
        />
      </span>
    );
  }
  return (
    <button
      onClick={() => !disabled && setEditing(true)}
      className="inline-block bg-yellow-100 border border-yellow-300 text-yellow-900 px-1.5 py-0.5 rounded-sm font-mono text-sm cursor-pointer hover:bg-yellow-200 transition-colors mx-0.5"
      title={`Click to specify: ${hint}`}
    >
      {fullMatch}
    </button>
  );
}

/**
 * Renders a claim body with both ``SPECIFY`` placeholder chips (yellow
 * clickable chip — editable) and flagged health issues (yellow ``<mark>``
 * span — informational, hover shows the message). The SPECIFY token
 * always wins on overlap: the chip is part of the editing flow, while
 * health highlights are advisory.
 */
function ClaimBodyRenderer({
  text, issues, onReplace, disabled,
}: {
  text: string;
  issues: HealthIssue[];
  onReplace: (original: string, replacement: string) => void;
  disabled: boolean;
}) {
  // 1. Find every SPECIFY placeholder range.
  const placeholderRanges: Array<{ start: number; end: number; full: string; hint: string }> = [];
  const specifyRe = /\u27e6SPECIFY:\s*([^\u27e7]+)\u27e7/g;
  let m: RegExpExecArray | null;
  while ((m = specifyRe.exec(text)) !== null) {
    placeholderRanges.push({
      start: m.index,
      end: m.index + m[0].length,
      full: m[0],
      hint: m[1].trim(),
    });
  }

  // 2. Find health-highlight ranges in the text (first occurrence each),
  // dropping any that overlap a SPECIFY chip so we don't double-decorate.
  const healthRanges = computeHighlightRanges(text, issues).filter(
    (r) => !placeholderRanges.some((p) => !(r.end <= p.start || r.start >= p.end)),
  );

  // 3. Weave into a single ordered list of spans.
  type DecoratedSegment =
    | { kind: 'placeholder'; start: number; end: number; full: string; hint: string }
    | { kind: 'highlight'; start: number; end: number; message: string };

  const boundaries: DecoratedSegment[] = [
    ...placeholderRanges.map((p): DecoratedSegment => ({ kind: 'placeholder', ...p })),
    ...healthRanges.map((h): DecoratedSegment => ({ kind: 'highlight', ...h })),
  ].sort((a, b) => a.start - b.start);

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  boundaries.forEach((seg, i) => {
    if (seg.start > cursor) {
      parts.push(
        <span key={`t-${cursor}`}>{text.slice(cursor, seg.start)}</span>,
      );
    }
    if (seg.kind === 'placeholder') {
      parts.push(
        <PlaceholderChip
          key={`p-${i}`}
          hint={seg.hint}
          fullMatch={seg.full}
          onReplace={onReplace}
          disabled={disabled}
        />,
      );
    } else {
      parts.push(
        <mark
          key={`h-${i}`}
          className="bg-yellow-100 border-b border-yellow-400 text-yellow-900 rounded-sm px-0.5"
          title={seg.message}
          data-testid="claim-health-highlight"
        >
          {text.slice(seg.start, seg.end)}
        </mark>,
      );
    }
    cursor = seg.end;
  });
  if (cursor < text.length) {
    parts.push(<span key={`t-end`}>{text.slice(cursor)}</span>);
  }

  return (
    <div
      className="whitespace-pre-wrap leading-loose"
      style={{ fontFamily: 'IBM Plex Serif, serif' }}
    >
      {parts}
    </div>
  );
}

function ClaimHealthBadge({
  result, claimNumber,
}: {
  result: HealthResult;
  claimNumber: number;
}) {
  const [open, setOpen] = useState(false);
  if (result.status === 'ok') {
    return (
      <span
        data-testid={`claim-health-ok-${claimNumber}`}
        title="No issues found"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs bg-green-100 border border-green-200 text-green-800"
      >
        <Check size={10} /> OK
      </span>
    );
  }
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`claim-health-warn-${claimNumber}`}
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setOpen(false)}
        title={
          result.issues
            .map((i) => `• ${i.message}`)
            .join('\n')
        }
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs bg-amber-100 border border-amber-200 text-amber-800 hover:bg-amber-200 transition-colors"
      >
        <AlertTriangle size={10} />
        {result.issues.length} issue{result.issues.length === 1 ? '' : 's'}
      </button>
      {open && (
        <div
          data-testid={`claim-health-issues-${claimNumber}`}
          className="absolute right-0 mt-1 w-80 z-20 bg-white border border-amber-200 rounded-sm shadow-md p-2 text-xs text-zinc-800"
          onMouseDown={(e) => e.preventDefault()}
        >
          <ul className="space-y-1">
            {result.issues.map((issue, idx) => (
              <li key={idx} className="flex items-start gap-1.5">
                <span
                  className={
                    'mt-0.5 inline-block h-1.5 w-1.5 rounded-full ' +
                    (issue.severity === 'advisory' ? 'bg-amber-400' : 'bg-amber-600')
                  }
                />
                <span>
                  <span className="font-medium text-amber-900">{issue.check_label}:</span>{' '}
                  {issue.message}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Severities that force the findings panel open on mount so the
 * drafter can't miss a blocker. Everything else (INFO, advisory,
 * note) starts collapsed. Lowercase set because upstream severity
 * strings arrive in mixed case ("Critical", "BLOCKER", "major"). */
const _AUTO_EXPAND_SEVERITIES = new Set([
  'blocker', 'major', 'critical', 'error',
]);


function ClaimQcFindings({
  claimNumber, findings,
}: {
  claimNumber: number;
  findings: Array<{
    claim_number: number;
    category?: string;
    severity?: string;
    message?: string;
    suggested_fix?: string;
  }>;
}) {
  const serious = React.useMemo(
    () => findings.some(f => _AUTO_EXPAND_SEVERITIES.has((f.severity || '').toLowerCase())),
    [findings],
  );
  // Reset the open/closed default whenever the findings array
  // identity changes (a new QC run produces a new list, even with
  // the same count). Using JSON.stringify keeps the deps deterministic
  // without having to deep-compare per-field.
  const findingsKey = React.useMemo(
    () => JSON.stringify(findings.map(f => [f.severity, f.message, f.suggested_fix])),
    [findings],
  );
  const [open, setOpen] = React.useState(serious);
  React.useEffect(() => {
    setOpen(serious);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findingsKey]);

  return (
    <div
      data-testid={`qc-findings-${claimNumber}`}
      className="mx-4 mt-3 border border-amber-200 rounded-sm bg-amber-50/60"
    >
      <button
        type="button"
        data-testid={`qc-findings-toggle-${claimNumber}`}
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-amber-50"
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-amber-900 inline-flex items-center gap-2">
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          QC findings · {findings.length}
          {serious && (
            <span className="text-[9px] font-medium text-red-800 bg-red-100 border border-red-200 rounded-sm px-1.5 py-0.5">
              needs review
            </span>
          )}
        </span>
        <span className="text-[10px] text-amber-800">
          {open ? 'Collapse' : 'Expand'}
        </span>
      </button>
      {open && (
        <ul className="space-y-1.5 px-3 pb-3">
          {findings.map((f, i) => {
            const sev = (f.severity || '').toLowerCase();
            const tone = sev === 'error' || sev === 'blocker' || sev === 'critical'
              ? 'text-red-700'
              : sev === 'major' || sev === 'warn' || sev === 'warning'
                ? 'text-amber-800'
                : 'text-zinc-700';
            return (
              <li key={i} className="text-[11px] flex items-start gap-1.5">
                <span className={`font-mono uppercase text-[9px] shrink-0 mt-0.5 ${tone}`}>
                  {sev || 'info'}
                </span>
                <div className="flex-1">
                  <div className="text-zinc-800">{f.message}</div>
                  {f.suggested_fix && (
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      Suggested fix: {f.suggested_fix}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function ClaimCard({ claim, matterId, onUpdate, readOnly, healthResult, qcFindings }: {
  claim: Claim; matterId: string; onUpdate: () => void; readOnly?: boolean;
  healthResult: HealthResult;
  qcFindings: Array<{
    claim_number: number | null;
    category?: string;
    severity?: string;
    message?: string;
    suggested_fix?: string;
  }>;
}) {
  // `readOnly` is set when Stage 3 is rendered in review mode from Stage 4.
  // The claim itself may not be frozen, but we should still lock everything.
  const locked = !!readOnly || claim.is_frozen;
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(claim.claim_text);
  const [editAnalysis, setEditAnalysis] = useState(claim.analysis);
  // Manual-edit mode — set when the drafter clicks Unfreeze on a
  // frozen claim. Forces the textarea visible, surfaces the amber
  // "QC will flag changes" notice, and swaps the Freeze button for
  // [Save & Re-freeze] + [Cancel]. Cleared on either of those two
  // actions completing successfully. Tracked independently of
  // editMode so a normal click-to-edit flow on an already-unfrozen
  // claim doesn't accidentally trip the Save & Re-freeze plumbing.
  const [manualEditMode, setManualEditMode] = useState(false);
  const [originalText, setOriginalText] = useState<string>(claim.claim_text);
  const [regenerating, setRegenerating] = useState(false);
  const [scopeLoading, setScopeLoading] = useState<string | null>(null);
  const [freezingClaim, setFreezingClaim] = useState(false);
  const [pendingRegen, setPendingRegen] = useState<any>(null);
  // Steer — free-text, dependency-aware edit. steerOpen shows the inline
  // instruction box; steerPreview holds the LLM's proposed before/after plus
  // its dependent-claim suggestions; acceptedDeps tracks which dependent
  // rewrites the drafter has ticked (defaults to every non-frozen suggestion).
  const [steerOpen, setSteerOpen] = useState(false);
  const [steerInput, setSteerInput] = useState('');
  const [steerLoading, setSteerLoading] = useState(false);
  const [steerPreview, setSteerPreview] = useState<api.SteerPreview | null>(null);
  const [acceptedDeps, setAcceptedDeps] = useState<Set<string>>(new Set());
  const [applyingSteer, setApplyingSteer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [restoringIdx, setRestoringIdx] = useState<number | null>(null);
  const historyRef = React.useRef<HTMLDivElement | null>(null);
  // Scroll the history panel into view when it opens. The panel is
  // rendered below the claim text + analysis, which on a long claim
  // sits way below the viewport — clicking History made the button
  // appear to do nothing because the panel was off-screen.
  useEffect(() => {
    if (showHistory && historyRef.current) {
      historyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showHistory]);
  // Two-step delete — mirrors the Home / Stage 1 delete UX.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletingClaim, setDeletingClaim] = useState(false);

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeletingClaim(true);
    setError(null);
    try {
      await api.deleteClaim(claim.id);
      onUpdate();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Delete failed');
    }
    setDeletingClaim(false);
    setConfirmDelete(false);
  };

  const history = Array.isArray(claim.version_history) ? claim.version_history : [];

  const handleRestore = async (idx: number) => {
    setRestoringIdx(idx);
    setError(null);
    try {
      await api.restoreClaimVersion(claim.id, idx);
      setShowHistory(false);
      onUpdate();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Restore failed');
    }
    setRestoringIdx(null);
  };

  const handleSaveText = async (newText: string) => {
    try {
      await api.updateClaim(claim.id, { claim_text: newText });
      onUpdate();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleReplacePlaceholder = async (original: string, replacement: string) => {
    const newText = editText.replace(original, replacement);
    setEditText(newText);
    await handleSaveText(newText);
  };

  const handleSaveAnalysis = async () => {
    try {
      await api.updateClaim(claim.id, { analysis: editAnalysis });
      onUpdate();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    setError(null);
    try {
      await api.updateClaim(claim.id, { analysis: editAnalysis });
      const result = await api.regenerateClaim(claim.id);
      setPendingRegen(result);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Regeneration failed');
    }
    setRegenerating(false);
  };

  const handleAcceptRegen = async () => {
    if (!pendingRegen) return;
    try {
      await api.acceptRegeneration(claim.id, {
        claim_text: pendingRegen.new_claim_text,
        analysis: pendingRegen.new_analysis,
      });
      setEditText(pendingRegen.new_claim_text);
      setEditAnalysis(pendingRegen.new_analysis);
      setPendingRegen(null);
      onUpdate();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Accept failed');
    }
  };

  const handleScope = async (direction: string) => {
    setScopeLoading(direction);
    setError(null);
    try {
      const result = await api.adjustScope(claim.id, direction);
      setPendingRegen({
        new_claim_text: result.new_claim_text,
        new_analysis: result.new_analysis,
        change_summary: result.change_summary,
      });
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Scope adjustment failed');
    }
    setScopeLoading(null);
  };

  const handleSteer = async () => {
    const instruction = steerInput.trim();
    if (!instruction) return;
    setSteerLoading(true);
    setError(null);
    try {
      const result = await api.steerClaim(claim.id, instruction);
      setSteerPreview(result);
      // Pre-tick every dependent suggestion the backend says isn't frozen.
      setAcceptedDeps(new Set(
        result.affected_dependents
          .filter(d => !d.is_frozen)
          .map(d => d.claim_id),
      ));
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Steering failed');
    }
    setSteerLoading(false);
  };

  const toggleDep = (depClaimId: string) => {
    setAcceptedDeps(prev => {
      const next = new Set(prev);
      if (next.has(depClaimId)) next.delete(depClaimId);
      else next.add(depClaimId);
      return next;
    });
  };

  const handleApplySteer = async () => {
    if (!steerPreview) return;
    setApplyingSteer(true);
    setError(null);
    try {
      const accepted = steerPreview.affected_dependents
        .filter(d => acceptedDeps.has(d.claim_id) && !d.is_frozen)
        .map(d => ({ claim_id: d.claim_id, modified_text: d.suggested_text }));
      await api.applySteer(claim.id, {
        modified_text: steerPreview.new_claim_text,
        analysis: steerPreview.new_analysis,
        accepted_dependent_updates: accepted,
      });
      setSteerPreview(null);
      setSteerInput('');
      setSteerOpen(false);
      setAcceptedDeps(new Set());
      onUpdate();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Apply failed');
    }
    setApplyingSteer(false);
  };

  const handleDiscardSteer = () => {
    setSteerPreview(null);
    setAcceptedDeps(new Set());
  };

  const handleFreeze = async () => {
    setFreezingClaim(true);
    try {
      await api.freezeClaim(claim.id);
      onUpdate();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Cannot freeze claim');
    }
    setFreezingClaim(false);
  };

  // ── Per-claim manual-edit lifecycle ──────────────────────────
  // Unfreeze → flip claim.is_frozen=false via the explicit
  // /unfreeze endpoint AND set local manualEditMode=true so the
  // UI surfaces the dedicated [Save & Re-freeze] + [Cancel]
  // workflow + amber notice, rather than dropping back into the
  // generic click-to-edit flow.
  const handleUnfreezeForManualEdit = async () => {
    setFreezingClaim(true);
    setError(null);
    try {
      await api.unfreezeClaim(matterId, claim.id);
      setOriginalText(claim.claim_text);
      setEditText(claim.claim_text);
      setEditMode(true);
      setManualEditMode(true);
      onUpdate();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Cannot unfreeze claim');
    } finally {
      setFreezingClaim(false);
    }
  };

  // Save & Re-freeze → PUT text + flip claim.is_frozen=true via
  // the toggle freeze endpoint (currently unfrozen → freezes).
  // No QC re-run, no Stage 3 stage-level changes; the amber notice
  // makes the trade-off explicit ("QC will flag changes on next
  // review").
  const handleSaveRefreezeManualEdit = async () => {
    if (!editText.trim()) {
      setError('Cannot save empty claim text.');
      return;
    }
    setFreezingClaim(true);
    setError(null);
    try {
      await api.updateClaim(claim.id, { claim_text: editText });
      await api.freezeClaim(claim.id);
      setManualEditMode(false);
      setEditMode(false);
      onUpdate();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save & Re-freeze failed');
    } finally {
      setFreezingClaim(false);
    }
  };

  // Cancel manual edit → revert local editText to the snapshot
  // taken at Unfreeze time and re-freeze. Best-effort PATCH first
  // so the server-side text matches what we just reverted to.
  const handleCancelManualEdit = async () => {
    setFreezingClaim(true);
    setError(null);
    try {
      setEditText(originalText);
      try { await api.updateClaim(claim.id, { claim_text: originalText }); } catch { /* tolerate */ }
      await api.freezeClaim(claim.id);
      setManualEditMode(false);
      setEditMode(false);
      onUpdate();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Cancel failed');
    } finally {
      setFreezingClaim(false);
    }
  };

  const isIndependent = claim.claim_type === 'independent';

  return (
    <div
      data-testid={`claim-card-${claim.claim_number}`}
      className={`border rounded-sm bg-white mb-4 transition-all ${claim.is_frozen ? 'border-green-200 bg-green-50/20' : 'border-zinc-200'}`}
      id={`claim-${claim.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50/50">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold text-zinc-900">Claim {claim.claim_number}</span>
          <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-xs text-zinc-600 capitalize">{claim.claim_type}</span>
          <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-xs text-zinc-600 capitalize">{claim.category}</span>
          {claim.placeholder_count > 0 && (
            <span className="px-2 py-0.5 bg-amber-100 border border-amber-200 rounded-sm text-xs text-amber-800 font-mono flex items-center gap-1">
              <Hash size={10} />
              {claim.placeholder_count}
            </span>
          )}
          {claim.is_frozen && (
            <span className="px-2 py-0.5 bg-green-100 border border-green-200 rounded-sm text-xs text-green-800 flex items-center gap-1">
              <Lock size={10} /> Frozen
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ClaimHealthBadge result={healthResult} claimNumber={claim.claim_number} />
          {isIndependent && !locked && (
            <>
              <button
                data-testid={`broaden-claim-${claim.claim_number}`}
                onClick={() => handleScope('Broaden')}
                disabled={!!scopeLoading}
                className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm transition-colors"
              >
                {scopeLoading === 'Broaden' ? <Loader2 size={12} className="animate-spin" /> : <Maximize2 size={12} />}
                Broaden
              </button>
              <button
                data-testid={`narrow-claim-${claim.claim_number}`}
                onClick={() => handleScope('Narrow')}
                disabled={!!scopeLoading}
                className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm transition-colors"
              >
                {scopeLoading === 'Narrow' ? <Loader2 size={12} className="animate-spin" /> : <Minimize2 size={12} />}
                Narrow
              </button>
            </>
          )}
          {/* Steer — free-text instruction, available on any editable claim
              (independent or dependent), unlike Narrow/Broaden which are
              independent-only one-step scope nudges. */}
          {!locked && (
            <button
              data-testid={`steer-claim-${claim.claim_number}`}
              onClick={() => { setSteerPreview(null); setSteerOpen(o => !o); }}
              disabled={steerLoading || applyingSteer}
              className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm transition-colors"
            >
              {steerLoading ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
              Steer
            </button>
          )}
          {history.length > 0 && (
            <button
              data-testid={`claim-history-${claim.claim_number}`}
              onClick={() => setShowHistory(h => !h)}
              className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm transition-colors"
              title={`${history.length} previous version${history.length !== 1 ? 's' : ''} retained`}
            >
              <HistoryIcon size={12} /> History ({history.length})
            </button>
          )}
          {/* Freeze / Unfreeze toggle.
              - If the claim is editable (not frozen, not review-mode): show
                "Freeze" (disabled while placeholders remain).
              - If the claim is frozen but Stage 3 itself is still open
                (drafter navigated here normally or unfroze Stage 3 from
                Stage 4): show "Unfreeze" so they can tweak it. The backend
                endpoint is already a toggle — same call, new label.
              - If Stage 3 is being viewed read-only from Stage 4 review,
                hide both (readOnly=true). */}
          {/* In manual-edit mode the Freeze / Unfreeze pair is
              replaced by dedicated [Save & Re-freeze] + [Cancel]
              buttons so the drafter sees a single, explicit
              "what does this commit" affordance. */}
          {!readOnly && manualEditMode && (
            <>
              <button
                data-testid={`save-refreeze-claim-${claim.claim_number}`}
                onClick={handleSaveRefreezeManualEdit}
                disabled={freezingClaim || !editText.trim()}
                className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-50 transition-colors"
              >
                {freezingClaim ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
                Save &amp; Re-freeze
              </button>
              <button
                data-testid={`cancel-edit-claim-${claim.claim_number}`}
                onClick={handleCancelManualEdit}
                disabled={freezingClaim}
                className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
          {!readOnly && !manualEditMode && !claim.is_frozen && (
            <button
              data-testid={`freeze-claim-${claim.claim_number}`}
              onClick={handleFreeze}
              disabled={claim.placeholder_count > 0 || freezingClaim}
              className={`flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium transition-colors ${
                claim.placeholder_count > 0 ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed' : 'bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-green-50 hover:text-green-800'
              }`}
            >
              {freezingClaim ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
              Freeze
            </button>
          )}
          {!readOnly && !manualEditMode && claim.is_frozen && (
            <button
              data-testid={`unfreeze-claim-${claim.claim_number}`}
              onClick={handleUnfreezeForManualEdit}
              disabled={freezingClaim}
              className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
              title="Unfreeze for manual editing — QC will flag changes on next review"
            >
              {freezingClaim ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
              Unfreeze
            </button>
          )}
          {!locked && (
            <button
              data-testid={`delete-claim-${claim.claim_number}`}
              onClick={handleDelete}
              onBlur={() => setConfirmDelete(false)}
              disabled={deletingClaim}
              title={confirmDelete ? 'Click again to confirm' : 'Delete this claim'}
              className={`flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium transition-colors ${
                confirmDelete
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-zinc-100 text-zinc-500 border border-zinc-200 hover:bg-red-50 hover:text-red-600'
              }`}
            >
              {deletingClaim
                ? <Loader2 size={12} className="animate-spin" />
                : <Trash2 size={12} />}
              {confirmDelete && !deletingClaim ? 'Confirm' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 text-sm text-red-800 flex items-center gap-1">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* QC findings — shown only when /stage_4/qc has been run and
          returned findings for this claim_number. Collapsible per
          claim so a 10-finding INFO-only set doesn't push the rest
          of the card off-screen. Auto-expanded when at least one
          finding is BLOCKER / MAJOR / CRITICAL / ERROR severity
          (the drafter must see those). Otherwise starts collapsed.
          Collapse state is local (not persisted) and resets each
          time the findings array identity changes — i.e. a new
          QC run rewrites the defaults. */}
      {qcFindings.length > 0 && (
        <ClaimQcFindings
          claimNumber={claim.claim_number}
          findings={qcFindings}
        />
      )}

      {/* Analysis block */}
      <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50/30" data-testid={`analysis-block-${claim.claim_number}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Analysis</span>
          {!locked && (
            <button
              data-testid={`regenerate-claim-${claim.claim_number}`}
              onClick={handleRegenerate}
              disabled={regenerating}
              className="text-xs text-[#002FA7] hover:text-[#002277] flex items-center gap-1 transition-colors"
            >
              {regenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Regenerate from analysis
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {isIndependent && (
            <div className="col-span-2">
              <label className="text-xs text-zinc-500 block mb-1">Distinguishing Features</label>
              <textarea
                data-testid={`analysis-dist-features-${claim.claim_number}`}
                value={editAnalysis.distinguishing_features || ''}
                onChange={(e) => setEditAnalysis({ ...editAnalysis, distinguishing_features: e.target.value })}
                onBlur={handleSaveAnalysis}
                disabled={locked}
                rows={3}
                className="w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:bg-zinc-50 resize-y min-h-[4rem]"
              />
            </div>
          )}
          <div className="col-span-2">
            <label className="text-xs text-zinc-500 block mb-1">Key Terms</label>
            <textarea
              data-testid={`analysis-key-terms-${claim.claim_number}`}
              value={Array.isArray(editAnalysis.key_terms) ? editAnalysis.key_terms.join(', ') : editAnalysis.key_terms || ''}
              onChange={(e) => setEditAnalysis({ ...editAnalysis, key_terms: e.target.value.split(',').map((s: string) => s.trim()) })}
              onBlur={handleSaveAnalysis}
              disabled={locked}
              rows={2}
              className="w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:bg-zinc-50 resize-y min-h-[3rem]"
              placeholder="comma-separated"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-zinc-500 block mb-1">Technical Effect</label>
            <textarea
              data-testid={`analysis-tech-effect-${claim.claim_number}`}
              value={editAnalysis.technical_effect || ''}
              onChange={(e) => setEditAnalysis({ ...editAnalysis, technical_effect: e.target.value })}
              onBlur={handleSaveAnalysis}
              disabled={locked}
              rows={2}
              className="w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:bg-zinc-50 resize-y min-h-[3rem]"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-zinc-500 block mb-1">Advantage</label>
            <textarea
              data-testid={`analysis-advantage-${claim.claim_number}`}
              value={editAnalysis.advantage || ''}
              onChange={(e) => setEditAnalysis({ ...editAnalysis, advantage: e.target.value })}
              onBlur={handleSaveAnalysis}
              disabled={locked}
              rows={2}
              className="w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:bg-zinc-50 resize-y min-h-[3rem]"
            />
          </div>
        </div>
      </div>

      {/* Claim text */}
      <div className="p-4" data-testid={`claim-text-${claim.claim_number}`}>
        {/* Amber notice — only in manual-edit mode. Sits above
            the textarea so the drafter sees the "QC will flag
            changes" warning before they start typing. */}
        {manualEditMode && (
          <div
            data-testid={`claim-manual-edit-notice-${claim.claim_number}`}
            className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-900 flex items-start gap-1.5"
          >
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-amber-600" />
            <span>
              Manual edit mode — QC will flag changes on next review.
              Other claims and the stage-level frozen state are
              unchanged.
            </span>
          </div>
        )}
        {locked ? (
          <ClaimBodyRenderer
            text={claim.claim_text}
            issues={healthResult.issues}
            onReplace={() => {}}
            disabled={true}
          />
        ) : (editMode || manualEditMode) ? (
          <textarea
            data-testid={`claim-text-edit-${claim.claim_number}`}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={() => {
              // In manual-edit mode the textarea stays open until
              // the drafter clicks Save & Re-freeze or Cancel —
              // auto-save on blur would persist half-typed edits
              // and the explicit-action contract is the whole
              // point of the spec. Outside manual-edit, behave
              // exactly as before.
              if (manualEditMode) return;
              handleSaveText(editText);
              setEditMode(false);
            }}
            rows={10}
            className="w-full bg-white border border-zinc-300 rounded-sm px-4 py-3 text-sm leading-loose focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
            autoFocus
          />
        ) : (
          <div
            onClick={() => !locked && setEditMode(true)}
            className="cursor-text hover:bg-zinc-50/50 transition-colors rounded-sm p-2 -m-2 min-h-[60px]"
          >
            <ClaimBodyRenderer
              text={editText}
              issues={healthResult.issues}
              onReplace={handleReplacePlaceholder}
              disabled={locked}
            />
          </div>
        )}
      </div>

      {/* Version history */}
      {showHistory && history.length > 0 && (
        <div
          ref={historyRef}
          data-testid={`claim-history-panel-${claim.claim_number}`}
          className="mx-4 mb-4 border border-zinc-200 rounded-sm bg-zinc-50/50 p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">
              Previous versions (last {history.length}, newest first)
            </span>
            <button
              onClick={() => setShowHistory(false)}
              className="text-xs text-zinc-400 hover:text-zinc-600"
            >Close</button>
          </div>
          <div className="space-y-2">
            {history.slice().reverse().map((v: any, reverseIdx: number) => {
              // history.slice().reverse() inverts order; original index = length - 1 - reverseIdx
              const origIdx = history.length - 1 - reverseIdx;
              return (
                <div
                  key={`${v.generated_at}-${origIdx}`}
                  data-testid={`claim-history-item-${claim.claim_number}-${origIdx}`}
                  className="border border-zinc-200 rounded-sm bg-white p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-500">
                      {v.generated_at ? new Date(v.generated_at).toLocaleString() : 'unknown time'}
                    </span>
                    {!locked && (
                      <button
                        data-testid={`restore-version-${claim.claim_number}-${origIdx}`}
                        onClick={() => handleRestore(origIdx)}
                        disabled={restoringIdx === origIdx}
                        className="text-xs flex items-center gap-1 px-2 py-1 bg-[#002FA7] text-white rounded-sm hover:bg-[#002277] disabled:opacity-50"
                      >
                        {restoringIdx === origIdx ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                        Restore
                      </button>
                    )}
                  </div>
                  <div
                    className="text-xs text-zinc-700 leading-relaxed max-h-32 overflow-y-auto whitespace-pre-wrap"
                    style={{ fontFamily: 'IBM Plex Serif, serif' }}
                  >
                    {v.claim_text}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending regeneration */}
      {pendingRegen && (
        <div className="mx-4 mb-4 border border-blue-200 rounded-sm bg-blue-50/50 p-4" data-testid={`regen-preview-${claim.claim_number}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-blue-800">New Draft</span>
            {pendingRegen.change_summary && (
              <span className="text-xs text-blue-600 italic">{pendingRegen.change_summary}</span>
            )}
          </div>
          <div className="text-sm text-zinc-800 leading-relaxed mb-3 whitespace-pre-wrap" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
            {pendingRegen.new_claim_text}
          </div>
          <div className="flex gap-2">
            <button
              data-testid={`accept-regen-${claim.claim_number}`}
              onClick={handleAcceptRegen}
              className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-3 py-1.5 text-xs font-medium transition-colors"
            >
              Accept new
            </button>
            <button
              data-testid={`keep-original-${claim.claim_number}`}
              onClick={() => setPendingRegen(null)}
              className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1.5 text-xs font-medium transition-colors"
            >
              Keep original
            </button>
          </div>
        </div>
      )}

      {/* Steer — inline instruction box (shown until a preview comes back) */}
      {steerOpen && !steerPreview && (
        <div
          className="mx-4 mb-4 border border-[#002FA7]/30 rounded-sm bg-blue-50/30 p-3"
          data-testid={`steer-input-${claim.claim_number}`}
        >
          <label className="text-xs font-medium text-[#002FA7] mb-1 block">
            Steering instruction
          </label>
          <textarea
            value={steerInput}
            onChange={e => setSteerInput(e.target.value)}
            placeholder="e.g. Add a limitation requiring the actuators to be synchronous. Remove the reference to linear guide rails. Make the preamble an apparatus claim."
            className="w-full text-sm bg-white border border-zinc-200 rounded-sm p-2 text-zinc-800 resize-y focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
            rows={3}
            autoFocus
          />
          <div className="flex gap-2 mt-2">
            <button
              data-testid={`steer-submit-${claim.claim_number}`}
              onClick={handleSteer}
              disabled={!steerInput.trim() || steerLoading}
              className="bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-50 rounded-sm px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1"
            >
              {steerLoading ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
              Apply steering
            </button>
            <button
              onClick={() => { setSteerOpen(false); setSteerInput(''); }}
              className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1.5 text-xs font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Steer — before/after diff + dependent-claim suggestions */}
      {steerPreview && (
        <div
          className="mx-4 mb-4 border border-amber-300 rounded-sm bg-amber-50/40 p-4"
          data-testid={`steer-preview-${claim.claim_number}`}
        >
          <div className="text-xs font-medium text-amber-800 mb-3">
            Review changes to Claim {steerPreview.claim_number}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-zinc-500 mb-1">Before</div>
              <div
                className="text-sm text-zinc-500 bg-white border border-zinc-200 rounded-sm p-3 line-through opacity-70 whitespace-pre-wrap"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
              >
                {steerPreview.original_claim_text}
              </div>
            </div>
            <div>
              <div className="text-xs text-green-700 mb-1">After</div>
              <div
                className="text-sm text-zinc-800 bg-green-50 border border-green-300 rounded-sm p-3 whitespace-pre-wrap"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
              >
                {steerPreview.new_claim_text}
              </div>
            </div>
          </div>
          {steerPreview.change_summary && (
            <div className="text-xs text-zinc-600 italic mt-2">{steerPreview.change_summary}</div>
          )}

          {steerPreview.affected_dependents.length > 0 && (
            <div className="mt-4 border-t border-amber-200 pt-3">
              <div className="text-xs text-amber-800 mb-2 flex items-center gap-1">
                <AlertTriangle size={12} />
                <span>
                  {steerPreview.affected_dependents.length} dependent claim(s) may need updating
                  {steerPreview.affected_dependents.some(d => d.is_frozen)
                    ? ` (${steerPreview.affected_dependents.filter(d => d.is_frozen).length} frozen — cannot be updated until unfrozen):`
                    : ':'}
                </span>
              </div>
              {steerPreview.affected_dependents.map(dep => (
                <div key={dep.claim_id} className="mb-3 text-sm" data-testid={`steer-dependent-${dep.claim_number}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-xs font-medium text-zinc-700 flex items-center gap-1">
                      Claim {dep.claim_number}
                      {dep.is_frozen && (
                        <span className="ml-1 px-1.5 py-0.5 bg-green-100 border border-green-200 rounded-sm text-green-800 flex items-center gap-1">
                          <Lock size={9} /> frozen
                        </span>
                      )}
                    </span>
                    {dep.reason && <span className="text-xs text-zinc-400 italic">{dep.reason}</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div
                      className="text-xs text-zinc-500 bg-white border border-zinc-200 rounded-sm p-2 whitespace-pre-wrap"
                      style={{ fontFamily: 'IBM Plex Serif, serif' }}
                    >
                      {dep.original_text}
                    </div>
                    <div
                      className="text-xs text-zinc-800 bg-blue-50 border border-blue-200 rounded-sm p-2 whitespace-pre-wrap"
                      style={{ fontFamily: 'IBM Plex Serif, serif' }}
                    >
                      {dep.suggested_text}
                    </div>
                  </div>
                  {dep.is_frozen ? (
                    <div
                      data-testid={`steer-dependent-locked-${dep.claim_number}`}
                      className="text-xs text-amber-800 bg-amber-100/70 border border-amber-300 rounded-sm px-2 py-1 mt-1 flex items-center gap-1"
                    >
                      <Lock size={11} />
                      Cannot update — Claim {dep.claim_number} is frozen. Unfreeze it first to apply this suggestion.
                    </div>
                  ) : (
                    <label className="text-xs flex items-center gap-1 mt-1 text-zinc-600 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`steer-dependent-accept-${dep.claim_number}`}
                        checked={acceptedDeps.has(dep.claim_id)}
                        onChange={() => toggleDep(dep.claim_id)}
                      />
                      Accept suggested update
                    </label>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <button
              data-testid={`steer-apply-${claim.claim_number}`}
              onClick={handleApplySteer}
              disabled={applyingSteer}
              className="bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-50 rounded-sm px-4 py-2 text-xs font-medium transition-colors flex items-center gap-1"
            >
              {applyingSteer ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Apply changes
            </button>
            <button
              data-testid={`steer-discard-${claim.claim_number}`}
              onClick={handleDiscardSteer}
              className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-4 py-2 text-xs font-medium transition-colors"
            >
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddClaimModal({ matterId, preset, claims, onClose, onAdded }: {
  matterId: string;
  preset: { kind: 'independent' | 'dependent'; parent_claim_number: number | null };
  claims: Claim[];
  onClose: () => void;
  onAdded: () => void;
}) {
  // Drafter-facing form for POST /matter/{id}/claim/add. The sidebar "+ Add"
  // buttons seed kind + parent; the modal lets the drafter change them and
  // write a plain-English instruction that the LLM turns into a new claim.
  const [kind, setKind] = useState<'independent' | 'dependent'>(preset.kind);
  const [parent, setParent] = useState<number | null>(preset.parent_claim_number);
  const [category, setCategory] = useState<string>(() => {
    // Default category: parent's category for deps, 'method' for new indeps.
    if (preset.kind === 'dependent' && preset.parent_claim_number) {
      const p = claims.find(c => c.claim_number === preset.parent_claim_number);
      if (p) return p.category;
    }
    return 'method';
  });
  const [instruction, setInstruction] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const independents = claims
    .filter(c => c.claim_type === 'independent')
    .sort((a, b) => a.claim_number - b.claim_number);

  const handleSubmit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await api.addClaim(matterId, {
        kind,
        category,
        parent_claim_number: kind === 'dependent' ? parent : null,
        instruction,
      });
      onAdded();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Failed to add claim');
    }
    setSubmitting(false);
  };

  const canSubmit = kind === 'independent' || (kind === 'dependent' && !!parent);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm"
      data-testid="add-claim-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-sm border border-zinc-200 shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Add claim
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 transition-colors"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {err && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800 flex items-center gap-1.5">
            <AlertTriangle size={14} /> {err}
          </div>
        )}

        <label className="text-xs text-zinc-500 block mb-1">Kind</label>
        <div className="flex gap-2 mb-4">
          {(['independent', 'dependent'] as const).map(k => (
            <button
              type="button"
              key={k}
              data-testid={`add-claim-kind-${k}`}
              onClick={() => setKind(k)}
              className={`flex-1 px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                kind === k ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]' : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
              }`}
            >
              {k === 'independent' ? 'New independent' : 'New dependent'}
            </button>
          ))}
        </div>

        {kind === 'dependent' && (
          <>
            <label className="text-xs text-zinc-500 block mb-1">Depends on</label>
            <select
              data-testid="add-claim-parent"
              value={parent ?? ''}
              onChange={(e) => {
                const num = e.target.value ? Number(e.target.value) : null;
                setParent(num);
                if (num) {
                  const p = claims.find(c => c.claim_number === num);
                  if (p) setCategory(p.category);
                }
              }}
              className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            >
              <option value="">Select an independent claim</option>
              {independents.map(ind => (
                <option key={ind.id} value={ind.claim_number}>
                  Claim {ind.claim_number} · {ind.category}
                </option>
              ))}
            </select>
          </>
        )}

        <label className="text-xs text-zinc-500 block mb-1">Category</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {['method', 'system', 'crm', 'apparatus'].map(cat => (
            <button
              type="button"
              key={cat}
              data-testid={`add-claim-category-${cat}`}
              onClick={() => setCategory(cat)}
              disabled={kind === 'dependent'}
              title={kind === 'dependent' ? 'Dependents inherit their parent\u2019s category' : undefined}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                category === cat
                  ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]'
                  : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
              } ${kind === 'dependent' ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {cat.toUpperCase()}
            </button>
          ))}
        </div>

        <label className="text-xs text-zinc-500 block mb-1">Instruction (optional)</label>
        <textarea
          data-testid="add-claim-instruction"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          rows={4}
          placeholder={kind === 'dependent'
            ? 'e.g. narrow by requiring the Kalman filter to update at 10 Hz'
            : 'e.g. add a system claim covering the sensor + processor combination'}
          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent resize-y min-h-[5rem]"
          maxLength={600}
        />

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="add-claim-submit"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add claim
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Stage3() {
  const {
    matterState, setMatterState,
    readOnlyStage, setReadOnlyStage,
    scrollToClaimId, setScrollToClaimId,
    setActiveStage,
  } = useAppStore();
  const matter = matterState?.matter;
  const matterId = matter?.id || '';
  // Stabilise the claims array reference across renders so the
  // useMemo health map below doesn't re-run on every parent re-render.
  const claims = useMemo(
    () => matterState?.claims || [],
    [matterState?.claims],
  );
  const s3 = matterState?.stages?.stage_3;
  const s3Content = s3?.content || {};
  // Read-only when the stage is frozen OR when the user arrived via
  // "Jump to claim" from Stage 4.
  const isReviewMode = readOnlyStage === 'stage_3';
  const isFrozen = !!s3?.is_frozen || isReviewMode;

  const [generating, setGenerating] = useState(false);
  const [freezingStage, setFreezingStage] = useState(false);
  // Async-claims Stage 3 polling: when USE_ASYNC_CLAIMS_STAGE_3
  // is ON, generateClaims returns a queued envelope and we
  // surface progress via this hook. The job_id is persisted to
  // localStorage so a refresh during a long-running generation
  // resumes polling against the same backend record (Sub-Chunk
  // 6 of the claims-async migration).
  const jobIdStorageKey = `claims-async:stage_3:job_id:${matterId}`;
  const [activeJobId, setActiveJobId] = React.useState<string | null>(() => {
    try {
      return window.localStorage.getItem(jobIdStorageKey);
    } catch {
      return null;
    }
  });
  const refreshMatter = useCallback(async () => {
    const data = await api.getMatter(matterId);
    setMatterState(data);
  }, [matterId, setMatterState]);
  const handleJobSettled = React.useCallback(
    async (final: api.JobStatus) => {
      // On terminal status (succeeded / failed / refused /
      // cancelled), drop the persisted handle so a next-time
      // mount doesn't resume an already-finished job, and pull
      // the fresh matter so the claim list re-renders.
      try {
        window.localStorage.removeItem(jobIdStorageKey);
      } catch { /* noop */ }
      if (final.status === 'succeeded' || final.status === 'finished') {
        await refreshMatter();
      }
      setGenerating(false);
    },
    [jobIdStorageKey, refreshMatter],
  );
  const jobStatusReturn = useJobStatus(activeJobId, {
    pollInterval: 2000,
    onSettled: handleJobSettled,
  });
  const liveJobStatus = jobStatusReturn.status;
  // Treat in-flight async generation as a "generating" state so
  // the existing in-flight UI (disabled Generate button, spinner,
  // etc.) covers both the inline and async paths.
  const asyncJobInFlight = !!activeJobId && !jobStatusReturn.isTerminal;
  React.useEffect(() => {
    if (asyncJobInFlight && !generating) setGenerating(true);
  }, [asyncJobInFlight, generating]);
  // epi compliance Item 2 — review acknowledgment gates the
  // "Continue to QC" button. AND-ed with canFreezeStage below.
  const persistedAckS3: string | null = (s3Content as any)?.acknowledged_at || null;
  const [ackAtS3, setAckAtS3] = useState<string | null>(persistedAckS3);
  useEffect(() => { setAckAtS3(persistedAckS3); }, [persistedAckS3]);
  const [activeClaimId, setActiveClaimId] = useState<string | null>(
    scrollToClaimId || claims[0]?.id || null,
  );
  const [error, setError] = useState<string | null>(null);
  // Structured gate-error from the Stage 3 backend's fail-closed
  // paths (412 novelty_missing / inventive_step_missing,
  // 409 category_not_confirmed). Shows a tailored banner with an
  // action button instead of the generic "Claim generation failed"
  // toast — pre-fix, those 4xxs all rendered the same useless
  // "Check LLM settings" copy.
  const [gateError, setGateError] = useState<{
    message: string;
    action: 'stage_2' | 'stage_2_5' | null;
  } | null>(null);

  // Collapsed sidebar state — persisted in localStorage so the preference
  // sticks across refreshes / matter switches. Default is expanded.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('stage3.sidebarCollapsed') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('stage3.sidebarCollapsed', sidebarCollapsed ? '1' : '0'); } catch {}
  }, [sidebarCollapsed]);

  // Add-claim modal state. preset carries the kind + parent hint from the
  // sidebar "+ Add …" button; the drafter can edit them in the dialog before
  // submitting.
  const [addClaimPreset, setAddClaimPreset] = useState<null | {
    kind: 'independent' | 'dependent';
    parent_claim_number: number | null;
  }>(null);

  // Scroll to a claim after mount if Stage 4 asked us to, then clear the
  // signal so subsequent navigations don't re-trigger it.
  useEffect(() => {
    if (!scrollToClaimId) return;
    setActiveClaimId(scrollToClaimId);
    // Give the DOM a tick to render then scroll into view.
    const t = setTimeout(() => {
      const el = document.getElementById(`claim-${scrollToClaimId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-[#002FA7]', 'ring-offset-2');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-[#002FA7]', 'ring-offset-2');
        }, 2500);
      }
      setScrollToClaimId(null);
    }, 120);
    return () => clearTimeout(t);
  }, [scrollToClaimId, setScrollToClaimId]);

  const handleGenerate = async () => {
    // Concurrency guard — the empty-state gate banner and any
    // auto-generation effect both route here, so a rapid double-
    // click (or a race between auto-fire and a user click) would
    // otherwise trigger two POSTs to /claims/generate. setGenerating
    // is the single flag both paths check.
    if (generating) return;
    setGenerating(true);
    setError(null);
    setGateError(null);
    try {
      const resp = await api.generateClaims(matterId);
      // Dual-path: when USE_ASYNC_CLAIMS_STAGE_3 is ON, the
      // endpoint returns the queued envelope and we poll via
      // useJobStatus instead of awaiting the synchronous result.
      // The hook drives a useEffect-managed poll loop; setting
      // activeJobId persists the id so a refresh-during-run can
      // resume polling (see jobIdStorageKey + the restore-from-
      // localStorage effect below).
      if (api.isAsyncJobEnvelope(resp)) {
        setActiveJobId(resp.job_id);
        try {
          window.localStorage.setItem(jobIdStorageKey, resp.job_id);
        } catch { /* localStorage may be unavailable; soldier on */ }
        // Don't await the result — the poll loop will refreshMatter
        // when status flips to succeeded.
        return;
      }
      await refreshMatter();
    } catch (e: any) {
      // Stage 3's fail-closed gates surface structured error
      // bodies. Route them to a tailored banner instead of the
      // generic "Check LLM settings" string.
      //
      // 409 → flat body: {error_code, message, suggested_category, ...}
      //       (returned via JSONResponse, not raise HTTPException)
      // 412 → {detail: {error_code, message}}
      //       (raise HTTPException(412, detail={...}))
      // Other → string detail or fallback.
      const status: number = e?.response?.status ?? 0;
      const body = e?.response?.data ?? {};
      const inner = (typeof body === 'object' && body && body.detail
        && typeof body.detail === 'object') ? body.detail : body;
      const code: string =
        (typeof inner?.error_code === 'string' && inner.error_code) || '';
      const msg: string =
        (typeof inner?.message === 'string' && inner.message) || '';
      if (status === 409 && code === 'category_not_confirmed') {
        setGateError({
          message:
            msg
            || "Confirm Claim 1 category in Stage 2.5 before generating claims.",
          action: 'stage_2_5',
        });
      } else if (status === 412 && code === 'novelty_missing') {
        setGateError({
          message:
            msg
            || "Stage 2 novelty analysis missing — generate and freeze it before running Stage 3.",
          action: 'stage_2',
        });
      } else if (status === 412 && code === 'inventive_step_missing') {
        setGateError({
          message:
            msg
            || "Stage 2 inventive step analysis missing — generate and freeze it before running Stage 3.",
          action: 'stage_2',
        });
      } else {
        // Final fallback — preserve whatever string detail the
        // server provided, else the legacy generic copy.
        const legacyDetail =
          typeof body?.detail === 'string' ? body.detail : '';
        setError(
          legacyDetail
          || msg
          || 'Claim generation failed. Check LLM settings.',
        );
      }
    }
    setGenerating(false);
  };

  const handleFreezeStage = async () => {
    setFreezingStage(true);
    setError(null);
    try {
      await api.freezeStage3(matterId);
      await refreshMatter();
      // Auto-advance to Stage 4 so "Continue to QC" actually continues
      // — previously the click froze the stage but left the drafter
      // on the post-freeze banner, requiring a second click in the
      // top-bar stage chip to move forward.
      setActiveStage('stage_4');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Cannot freeze Stage 3');
    }
    setFreezingStage(false);
  };

  // Run-QC in-place. The existing /stage_4/qc endpoint runs matter-wide
  // QC and stores findings under stage_3.content.qc_findings indexed by
  // claim_number, so we can surface them right on each ClaimCard here
  // without jumping into Stage 4. Drafters iterate faster this way:
  // run QC → fix → regenerate → run QC again.
  const [runningQc, setRunningQc] = useState(false);
  const [qcResultMessage, setQcResultMessage] = useState<string | null>(null);
  const handleRunQc = async () => {
    setRunningQc(true);
    setQcResultMessage(null);
    setError(null);
    try {
      const res = await api.runQc(matterId);
      await refreshMatter();
      const n = Array.isArray(res?.findings) ? res.findings.length : 0;
      // Split per-claim vs category-wide so the banner is honest
      // about where the drafter has to look. An LLM that returns
      // claim_number=0 for every finding would otherwise say "see
      // claim cards below" even though every finding rendered in
      // the General card at the top.
      let perClaim = 0;
      let general = 0;
      for (const f of res?.findings || []) {
        const raw = (f as any)?.claim_number;
        let cn: number | null = null;
        if (typeof raw === 'number' && Number.isFinite(raw)) cn = Math.trunc(raw);
        else if (typeof raw === 'string') {
          const m = raw.match(/\d+/);
          if (m) { const p = parseInt(m[0], 10); if (Number.isFinite(p)) cn = p; }
        }
        if (cn !== null && cn > 0) perClaim++; else general++;
      }
      if (n === 0) {
        setQcResultMessage('QC pass: no findings.');
      } else if (perClaim > 0 && general > 0) {
        setQcResultMessage(
          `QC found ${n} finding${n === 1 ? '' : 's'} — ${perClaim} on specific claims, ${general} general.`,
        );
      } else if (perClaim > 0) {
        setQcResultMessage(`QC found ${n} finding${n === 1 ? '' : 's'} — see claim cards below.`);
      } else {
        setQcResultMessage(`QC found ${n} finding${n === 1 ? '' : 's'} — see the General QC card below.`);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'QC run failed.');
    }
    setRunningQc(false);
  };

  // "Freeze All Claims" — bulk-freeze every claim with zero placeholders
  // without advancing to Stage 4. The drafter then clicks Continue to QC
  // separately, which hits the original freezeStage3 gate.
  const [freezingAll, setFreezingAll] = useState(false);
  const [bulkFreezeResult, setBulkFreezeResult] = useState<null | {
    frozen: number; skipped_with_placeholders: number[];
  }>(null);
  const handleFreezeAllClaims = async () => {
    setFreezingAll(true);
    setError(null);
    setBulkFreezeResult(null);
    try {
      const result = await api.freezeAllClaims(matterId);
      setBulkFreezeResult({
        frozen: Number(result?.frozen) || 0,
        skipped_with_placeholders: Array.isArray(result?.skipped_with_placeholders)
          ? result.skipped_with_placeholders : [],
      });
      await refreshMatter();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to freeze all claims');
    }
    setFreezingAll(false);
  };

  // ─── Claim health evaluation ────────────────────────────────────
  // Memoised keyed on the claim texts so typing in the edit box doesn't
  // re-compute for every claim on every keystroke (edit happens inside
  // ClaimCard's local state and only syncs to claims[] on save).
  const idfTexts = useMemo(() => idfTextsFromState(matterState), [matterState]);
  const healthByClaimId = useMemo(() => {
    const map = new Map<string, HealthResult>();
    for (const c of claims) {
      map.set(c.id, evaluateClaimHealth(c, claims, { idfTexts }));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, idfTexts]);

  // QC findings come back from /stage_4/qc and live on
  // stage_3.content.qc_findings. Bucket them by claim_number for
  // per-card rendering and keep a separate list of claim_number-0
  // (category-wide) findings that render as a top-level card.
  // Anthropic and GPT-4o both occasionally emit numeric-string
  // claim_numbers or descriptive strings despite the prompt asking
  // for plain int — handle those defensively here too.
  const { qcFindingsByClaimNumber, qcGeneralFindings } = useMemo(() => {
    const map = new Map<number, any[]>();
    const general: any[] = [];
    const findings = (matterState?.stages?.stage_3?.content as any)?.qc_findings;
    if (Array.isArray(findings)) {
      for (const f of findings) {
        const raw = f?.claim_number;
        let n: number | null = null;
        if (typeof raw === 'number' && Number.isFinite(raw)) {
          n = Math.trunc(raw);
        } else if (typeof raw === 'string' && raw.trim()) {
          const match = raw.match(/\d+/);
          if (match) {
            const parsed = parseInt(match[0], 10);
            if (Number.isFinite(parsed)) n = parsed;
          }
        }
        if (n === null || n <= 0) {
          general.push(f);
          continue;
        }
        if (!map.has(n)) map.set(n, []);
        map.get(n)!.push(f);
      }
    }
    return { qcFindingsByClaimNumber: map, qcGeneralFindings: general };
  }, [matterState]);

  if (claims.length === 0) {
    // Two distinct empty states:
    //   1. Stage 2 NOT frozen — the drafter shouldn't be here yet.
    //      Keep the red gate banner: this is a genuine blocked
    //      precondition, not a welcome screen.
    //   2. Stage 2 frozen, no claims yet — the normal first-visit
    //      path. Show a clean welcome card in the neutral/blue
    //      palette used elsewhere. Previously we reused the red
    //      StageGateBanner for this which made a fresh matter
    //      look like an error state.
    const stage2Frozen = !!(matterState?.stages as any)?.stage_2?.is_frozen;
    const jurisdiction = matter?.jurisdiction || '';

    if (!stage2Frozen) {
      return (
        <div className="max-w-3xl mx-auto pt-20 pb-12 px-6">
          <h1
            data-testid="stage-3-title"
            className="text-3xl font-semibold tracking-tight text-zinc-900 mb-1"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Stage 3 — Claim Drafting
          </h1>
          <p className="text-sm text-zinc-500 mb-4">Generate and refine patent claims</p>
          <StageGateBanner
            testId="stage-3-gate-banner"
            status="blocked"
            message="Freeze Stage 2 (Novelty & Inventive Step) before drafting claims."
            className="mb-4"
          />
        </div>
      );
    }

    // Welcome / first-visit state. Neutral card, blue primary button,
    // zero red. Error banner only surfaces beneath the card when a
    // previous Generate attempt failed.
    return (
      <div className="max-w-3xl mx-auto pt-16 pb-12 px-6">
        <h1
          data-testid="stage-3-title"
          className="text-3xl font-semibold tracking-tight text-zinc-900 mb-1"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          Stage 3 — Claim Drafting
        </h1>
        <p className="text-sm text-zinc-500 mb-6">Generate and refine patent claims</p>

        <div
          data-testid="stage-3-welcome"
          className="border border-zinc-200 rounded-sm bg-white p-8 text-center space-y-4"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 text-[#002FA7] mx-auto">
            <Zap size={20} />
          </div>
          <h2 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Ready to draft claims
          </h2>
          <p className="text-sm text-zinc-600 max-w-md mx-auto leading-relaxed">
            Your Novelty and Inventive Step analysis is complete.
            Generate your initial claim set to get started.
          </p>
          <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-widest">
            Jurisdiction: <span className="text-zinc-700">{jurisdiction || '—'}</span>
          </div>
          <button
            data-testid="stage-3-welcome-generate"
            onClick={handleGenerate}
            disabled={generating}
            className="mt-2 bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-5 py-2.5 text-sm font-medium transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          >
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            {generating
              ? 'Generating claims…'
              : error ? 'Retry' : 'Generate Initial Claim Set'}
          </button>
          {generating && (
            <p className="text-[11px] text-zinc-500 italic">
              This usually takes 20-40 seconds.
            </p>
          )}
        </div>

        {gateError && !generating && (
          <div
            data-testid="stage-3-gate-error"
            className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">{gateError.message}</div>
                {gateError.action && (
                  <button
                    type="button"
                    data-testid={`stage-3-gate-go-${gateError.action}`}
                    onClick={() => {
                      setGateError(null);
                      setActiveStage(gateError.action!);
                    }}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-sm px-2.5 py-1 transition-colors"
                  >
                    Open {gateError.action === 'stage_2_5' ? 'Stage 2.5' : 'Stage 2'}
                    <ArrowRight size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {error && !generating && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  const independentClaims = claims.filter(c => c.claim_type === 'independent');
  const allIndependentFrozen = independentClaims.every(c => c.is_frozen);
  const totalPlaceholders = claims.reduce((sum, c) => sum + c.placeholder_count, 0);
  const canFreezeStage = allIndependentFrozen && totalPlaceholders === 0;

  // Jump-back handler for sidebar STAGES list. Only frozen / earlier stages
  // should be clickable; Stage3Sidebar already enforces that via accessible.
  const handleJumpToStage = (s: Stage) => {
    if (s === 'stage_3') return;
    setReadOnlyStage(null);
    setActiveStage(s);
  };

  return (
    <div className="h-screen pt-14 flex overflow-hidden bg-white" data-testid="stage-3-layout">
      <Stage3Sidebar
        claims={claims}
        activeClaimId={activeClaimId}
        onSelect={(id) => {
          setActiveClaimId(id);
          // Scroll the main pane to the claim card — without this the
          // sidebar tree highlights the row but the page sits still and
          // the drafter has to hunt for it on long claim sets.
          requestAnimationFrame(() => {
            const el = document.getElementById(`claim-${id}`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // Flash a ring so the jump destination is obvious.
              el.classList.add('ring-2', 'ring-[#002FA7]', 'ring-offset-2');
              setTimeout(() => {
                el.classList.remove('ring-2', 'ring-[#002FA7]', 'ring-offset-2');
              }, 1600);
            }
          });
        }}
        matterState={matterState}
        onJumpToStage={handleJumpToStage}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(v => !v)}
        onAddClaimFor={(preset) => setAddClaimPreset(preset)}
        readOnly={isFrozen}
        healthByClaimId={healthByClaimId}
        qcFindingsByClaimNumber={qcFindingsByClaimNumber}
        onRelink={async () => {
          // Best-effort: surface any backend error in the
          // existing stage-level error banner; the relink
          // endpoint is idempotent so a transient failure
          // doesn't leave the matter in a bad state.
          try {
            await api.relinkClaims(matterId);
            await refreshMatter();
          } catch (e: any) {
            setError(e?.response?.data?.detail || 'Re-link failed');
          }
        }}
      />
      {addClaimPreset && (
        <AddClaimModal
          matterId={matterId}
          preset={addClaimPreset}
          claims={claims}
          onClose={() => setAddClaimPreset(null)}
          onAdded={async () => {
            setAddClaimPreset(null);
            await refreshMatter();
          }}
        />
      )}
      <div className="flex-1 overflow-y-auto p-8 md:p-12">
        {/* Width clamp: keep the 4xl line length when the sidebar is
            expanded (readable paragraph width), but drop to full
            width when the sidebar is collapsed so the drafter's
            explicit "give me more room" gesture actually produces
            more room. QC findings and long claim text benefit most
            from the extra horizontal real estate. */}
        <div className={sidebarCollapsed ? 'w-full' : 'max-w-4xl'}>
          {!isFrozen && (() => {
            // Gate banner computed at the stage-level so the drafter
            // sees a single top-of-page summary in the same visual
            // language used in Stage 1/2/4. Tier-2 QC findings =
            // ``critical`` severity on any persisted finding; they
            // surface as a warning (can proceed, should review)
            // rather than a hard block so the drafter can ignore and
            // freeze anyway if they disagree with the LLM's call.
            const unfrozenIndepsForBanner = independentClaims.filter(c => !c.is_frozen).length;
            const qcCriticalCount = [
              ...qcGeneralFindings,
              ...Array.from(qcFindingsByClaimNumber.values()).flat(),
            ].filter(f => {
              const s = (f?.severity || '').toString().toLowerCase();
              return s === 'critical' || s === 'tier_2' || s === 'tier2';
            }).length;
            if (unfrozenIndepsForBanner > 0 || totalPlaceholders > 0) {
              const parts: string[] = [];
              if (unfrozenIndepsForBanner > 0) {
                parts.push(
                  `freeze ${unfrozenIndepsForBanner} independent claim${unfrozenIndepsForBanner === 1 ? '' : 's'}`,
                );
              }
              if (totalPlaceholders > 0) {
                parts.push(
                  `resolve ${totalPlaceholders} placeholder${totalPlaceholders === 1 ? '' : 's'}`,
                );
              }
              return (
                <StageGateBanner
                  testId="stage-3-gate-banner"
                  status="blocked"
                  message={`Review your claims and ${parts.join(' and ')} before advancing to QC.`}
                  className="mb-4"
                />
              );
            }
            if (qcCriticalCount > 0) {
              return (
                <StageGateBanner
                  testId="stage-3-gate-banner"
                  status="warning"
                  message={`Your last QC found ${qcCriticalCount} structural issue${qcCriticalCount === 1 ? '' : 's'}. Review before freezing.`}
                  primaryAction={{ label: 'Run QC', onClick: handleRunQc, busy: runningQc }}
                  className="mb-4"
                />
              );
            }
            return null;
          })()}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 data-testid="stage-3-title" className="text-2xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Claim Drafting
              </h1>
              <p className="text-sm text-zinc-500">{claims.length} claims · {totalPlaceholders} placeholder{totalPlaceholders !== 1 ? 's' : ''}</p>
            </div>
            {!isFrozen && (() => {
              // Two-button layout (drafter request): a stand-alone
              // "Freeze All Claims" that bulk-freezes every clean claim
              // without advancing, plus a separate "Continue to QC" that
              // is the original gate (all independents frozen + zero
              // placeholders across the set, PRD §8.6).
              const unfrozenClaims = claims.filter(c => !c.is_frozen);
              const unfrozenWithoutPlaceholders = unfrozenClaims.filter(
                c => c.placeholder_count === 0,
              );
              const unfrozenIndeps = independentClaims.filter(c => !c.is_frozen);
              const reasons: string[] = [];
              if (unfrozenIndeps.length > 0) {
                reasons.push(
                  `${unfrozenIndeps.length} independent claim${unfrozenIndeps.length === 1 ? '' : 's'} not frozen ` +
                  `(${unfrozenIndeps.map(c => '#' + c.claim_number).join(', ')})`,
                );
              }
              if (totalPlaceholders > 0) {
                reasons.push(
                  `${totalPlaceholders} unresolved placeholder${totalPlaceholders === 1 ? '' : 's'} remaining`,
                );
              }
              const blocked = reasons.join(' · ');
              const canBulkFreeze = unfrozenWithoutPlaceholders.length > 0;
              return (
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="stage-3-run-qc-button"
                      onClick={handleRunQc}
                      disabled={runningQc || claims.length === 0}
                      title="Run the Stage 4 QC review now so you can fix issues before freezing. Findings show inline on each claim card."
                      className="rounded-sm px-3 py-2 text-sm font-medium transition-colors flex items-center gap-2 border bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                    >
                      {runningQc ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                      {runningQc ? 'Running QC…' : 'Run QC'}
                    </button>
                    <button
                      data-testid="freeze-all-claims-button"
                      onClick={handleFreezeAllClaims}
                      disabled={!canBulkFreeze || freezingAll}
                      title={
                        canBulkFreeze
                          ? `Freeze ${unfrozenWithoutPlaceholders.length} clean claim${unfrozenWithoutPlaceholders.length === 1 ? '' : 's'} in one click. Claims with placeholders are skipped.`
                          : 'Nothing to freeze — every clean claim is already frozen'
                      }
                      className={`rounded-sm px-3 py-2 text-sm font-medium transition-colors flex items-center gap-2 border ${
                        canBulkFreeze
                          ? 'bg-white text-[#002FA7] border-[#002FA7] hover:bg-blue-50'
                          : 'bg-zinc-50 text-zinc-400 border-zinc-200 cursor-not-allowed'
                      }`}
                    >
                      {freezingAll ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                      Freeze All Claims
                    </button>
                    <button
                      data-testid="freeze-stage-3-button"
                      onClick={handleFreezeStage}
                      disabled={!canFreezeStage || !ackAtS3 || freezingStage}
                      title={
                        blocked
                          || (!ackAtS3 ? 'Check the review-acknowledged box to continue' : 'All gates clear — advance to Stage 4 QC')
                      }
                      className={`rounded-sm px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
                        canFreezeStage && ackAtS3 ? 'bg-[#002FA7] text-white hover:bg-[#002277]' : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                      }`}
                    >
                      {freezingStage ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                      Continue to QC
                    </button>
                  </div>
                  {/* epi compliance Item 2 — review acknowledgment
                      gates the "Continue to QC" button above. The
                      checkbox lives on its own row so the "Freeze
                      All Claims" bulk action keeps its visual
                      association with the Continue button. */}
                  <StageAcknowledgment
                    matterId={matterId}
                    stage="stage_3"
                    initialAcknowledgedAt={ackAtS3}
                    onChange={setAckAtS3}
                    disabled={!!isFrozen}
                    onError={setError}
                  />
                  {bulkFreezeResult && (
                    <span
                      data-testid="bulk-freeze-result"
                      className={`text-[11px] rounded-sm px-2 py-0.5 border ${
                        bulkFreezeResult.skipped_with_placeholders.length > 0
                          ? 'text-amber-800 bg-amber-50 border-amber-200'
                          : 'text-green-800 bg-green-50 border-green-200'
                      }`}
                    >
                      Froze {bulkFreezeResult.frozen} claim{bulkFreezeResult.frozen === 1 ? '' : 's'}
                      {bulkFreezeResult.skipped_with_placeholders.length > 0 &&
                        ` · skipped ${bulkFreezeResult.skipped_with_placeholders.length} with placeholders ` +
                        `(${bulkFreezeResult.skipped_with_placeholders.map(n => '#' + n).join(', ')})`}
                    </span>
                  )}
                  {blocked && (
                    <span
                      data-testid="freeze-stage-3-blocked-reason"
                      className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-0.5"
                    >
                      Continue to QC blocked: {blocked}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
              <AlertTriangle size={16} /> {error}
            </div>
          )}
          {qcResultMessage && (
            <div
              data-testid="stage-3-qc-result"
              className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-800 flex items-center justify-between"
            >
              <span>{qcResultMessage}</span>
              <button
                type="button"
                onClick={() => setQcResultMessage(null)}
                className="text-blue-700 hover:text-blue-900"
                aria-label="Dismiss"
              >
                <X size={12} />
              </button>
            </div>
          )}
          {/* General QC findings — findings whose claim_number is 0
              (or missing / unparseable) don't tie to a specific card,
              so they render once at the top of the main pane. This
              catches the common LLM behaviour of returning
              category-wide issues without attaching them to a claim
              (especially Anthropic when the message describes a
              pattern across multiple claims). */}
          {qcGeneralFindings.length > 0 && (
            <div
              data-testid="stage-3-qc-general-findings"
              className="mb-4 border border-amber-200 rounded-sm bg-amber-50/60 p-3"
            >
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-900 mb-1.5">
                General QC findings · {qcGeneralFindings.length}
              </div>
              <p className="text-[11px] text-zinc-600 mb-2">
                Findings that apply to the whole claim set, not a single claim.
              </p>
              <ul className="space-y-1.5">
                {qcGeneralFindings.map((f, i) => {
                  const sev = (f?.severity || '').toLowerCase();
                  const tone = sev === 'critical' || sev === 'error'
                    ? 'text-red-700'
                    : (sev === 'warning' || sev === 'warn')
                      ? 'text-amber-800'
                      : 'text-zinc-700';
                  return (
                    <li key={i} className="text-[11px] flex items-start gap-1.5">
                      <span className={`font-mono uppercase text-[9px] shrink-0 mt-0.5 ${tone}`}>
                        {sev || 'info'}
                      </span>
                      <div className="flex-1">
                        {f?.category && (
                          <span className="text-[10px] text-zinc-500 mr-1">[{f.category}]</span>
                        )}
                        <span className="text-zinc-800">{f?.message}</span>
                        {f?.suggested_fix && (
                          <div className="text-[10px] text-zinc-500 mt-0.5">
                            Suggested fix: {f.suggested_fix}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {isReviewMode && (
            <div
              data-testid="stage-3-review-banner"
              className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-between text-sm text-blue-900"
            >
              <span className="flex items-center gap-2">
                <Eye size={16} /> Review mode — claims are read-only while you check QC findings.
              </span>
              <button
                data-testid="stage-3-exit-review"
                onClick={() => { setReadOnlyStage(null); setActiveStage('stage_4'); }}
                className="text-xs px-2 py-1 bg-white text-[#002FA7] border border-blue-200 rounded-sm hover:bg-blue-100 transition-colors"
              >
                Back to QC
              </button>
            </div>
          )}
          {/* Async-claims live progress + refusal banner. Visible
              only while a Stage 3 RQ job is in flight or just
              terminated in a non-success state — succeeded jobs
              fall through to the existing claim-list render. */}
          {(() => {
            if (!liveJobStatus) return null;
            const prog = liveJobStatus.progress;
            const message =
              prog?.message ||
              (liveJobStatus.status === 'queued' ? 'Queued for worker…' :
               liveJobStatus.status === 'running' ? 'Worker is preparing…' :
               'Status unknown');
            // Don't render once we've settled successfully —
            // refreshMatter will re-render with the new claim list.
            if (liveJobStatus.status === 'succeeded' ||
                liveJobStatus.status === 'finished') {
              return null;
            }
            if (liveJobStatus.status === 'refused_all_providers') {
              return (
                <div
                  data-testid="stage-3-refused-all-providers"
                  className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-sm"
                >
                  <div className="text-sm font-medium text-red-800 mb-1">
                    Every model in the capable-tier chain refused this
                    prompt on content-policy grounds.
                  </div>
                  <div className="text-xs text-red-700 leading-snug">
                    {liveJobStatus.error ||
                      'No provider was able to produce a claim set.'}
                    <br />
                    Contact an admin to review the chain configuration
                    or to enable a BLOCK_NONE safety override on a
                    fallback entry.
                  </div>
                </div>
              );
            }
            if (liveJobStatus.status === 'failed' ||
                liveJobStatus.status === 'cancelled' ||
                liveJobStatus.status === 'stopped') {
              return (
                <div
                  data-testid="stage-3-async-job-failed"
                  className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-800"
                >
                  Async generation {liveJobStatus.status}:{' '}
                  {liveJobStatus.error || 'unknown error'} — retry from
                  the Generate button, or check Settings → Admin →
                  Recent stage jobs for details.
                </div>
              );
            }
            // queued / running / started → progress badge.
            return (
              <div
                data-testid="stage-3-async-job-progress"
                data-step={prog?.step || liveJobStatus.status}
                className="mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-800 flex flex-wrap items-center gap-2"
              >
                <span className="font-mono uppercase tracking-wide text-[10px]">
                  {liveJobStatus.status}
                </span>
                <span>{message}</span>
                {prog?.provider && (
                  <span
                    className="ml-1 px-1.5 py-0.5 rounded-sm bg-white border border-blue-300 text-[10px] font-mono"
                    data-testid="stage-3-async-provider-chip"
                  >
                    {prog.provider}
                  </span>
                )}
                {(prog?.fallback_count ?? 0) > 0 && (
                  <span
                    className="ml-1 px-1.5 py-0.5 rounded-sm bg-amber-100 border border-amber-300 text-[10px] text-amber-800"
                    data-testid="stage-3-async-fallback-chip"
                  >
                    Switched to fallback provider
                  </span>
                )}
              </div>
            );
          })()}
          {!isReviewMode && s3?.is_frozen && (
            <div
              data-testid="stage-3-frozen-banner"
              className="mb-6 p-4 bg-green-50 border border-green-200 rounded-sm flex items-center justify-between gap-2 text-sm text-green-800"
            >
              <span className="flex items-center gap-2">
                <CheckCircle size={18} /> Stage 3 frozen — proceed to QC & Export
              </span>
              <button
                type="button"
                data-testid="stage-3-continue-to-stage-4"
                onClick={() => { setReadOnlyStage(null); setActiveStage('stage_4'); }}
                className="rounded-sm px-3 py-1.5 text-xs font-medium bg-[#002FA7] text-white hover:bg-[#002277] inline-flex items-center gap-1.5"
              >
                Continue to QC &amp; Export <ArrowRight size={12} />
              </button>
            </div>
          )}
          {(() => {
            // Content-policy fallback badge — surfaces "completed
            // via <provider> (content-policy fallback)" when the
            // tier chain advanced past its primary entry. Set by
            // the Stage 3 handler via _upsert_stage_fallback_metadata
            // immediately after the LLM call returned. Informational
            // only — claim generation succeeded; this just tells the
            // drafter that the model was a fallback (so they can
            // review more carefully if needed).
            const c = (s3?.content as any) || {};
            if (!c.used_fallback) return null;
            const provider = String(c.fallback_provider || 'fallback');
            const from = String(c.fallback_from || '');
            return (
              <div
                data-testid="stage-3-fallback-badge"
                className="mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-800 inline-flex items-center gap-2"
                title={`Original primary: ${from}`}
              >
                <span className="font-mono uppercase tracking-wide text-[10px]">
                  fallback
                </span>
                <span>
                  Completed via {provider}
                  {from ? ` (content-policy fallback from ${from})` : ''}
                </span>
              </div>
            );
          })()}
          {claims.map(claim => {
            const health = healthByClaimId.get(claim.id) || {
              claim_id: claim.id,
              status: 'ok' as const,
              issues: [],
              failed_checks: [],
            };
            return (
              <ClaimCard
                key={claim.id}
                claim={claim}
                matterId={matterId}
                onUpdate={refreshMatter}
                readOnly={isReviewMode}
                healthResult={health}
                qcFindings={qcFindingsByClaimNumber.get(claim.claim_number) || []}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

